import { useEffect, useRef, useState } from "react";
import Player from "@vimeo/player";
import { RotateCcw } from "lucide-react";
import { useI18n } from "@/i18n";

/**
 * Privacy-first Vimeo embed. Renders an iframe (absolute, fills the parent's
 * aspect-ratio container) plus SDK wiring so that when a video ENDS it shows a
 * custom "Replay video" overlay (no loop, no "more videos" end-screen, no
 * outbound links). Clicking Replay reuses the SAME player instance: it seeks to
 * 0 and plays inside the user's gesture (iOS/Android autoplay-safe).
 * The parent must be `position: relative` with the desired aspect ratio.
 */
export function VimeoPlayer({ videoId, title, lazy = false, testid }) {
  const { t } = useI18n();
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const [ended, setEnded] = useState(false);
  const [replaying, setReplaying] = useState(false);

  useEffect(() => {
    if (!iframeRef.current) return undefined;
    const player = new Player(iframeRef.current);
    playerRef.current = player;

    // On end: only mark the video as ended so we can show the custom Replay
    // control. Do NOT seek/pause/play here — automatically calling
    // setCurrentTime()/pause() on the "ended" state fights the subsequent
    // user-initiated replay and interrupts play()'s promise (silent no-op).
    const onEnded = () => setEnded(true);
    // When (re)playback actually starts, hide the overlay.
    const onPlay = () => {
      setEnded(false);
      setReplaying(false);
    };

    player.on("ended", onEnded);
    player.on("play", onPlay);
    return () => {
      // Only detach our listeners. Do NOT call player.destroy() — it removes the
      // iframe element that React owns (and React 18 StrictMode runs effects twice
      // in dev, which would otherwise delete the player permanently).
      try {
        player.off("ended", onEnded);
        player.off("play", onPlay);
      } catch (e) {
        /* ignore */
      }
    };
  }, [videoId]);

  // Runs directly inside the user's click/tap gesture so iOS Safari / Android
  // autoplay restrictions don't block play(). Seek to 0 first, then play; the
  // "play" event listener hides the overlay only once playback truly starts.
  const replay = async () => {
    const p = playerRef.current;
    if (!p || replaying) return;
    setReplaying(true);
    try {
      await p.setCurrentTime(0);
      await p.play();
      // Success: hide overlay immediately (the play listener also covers this).
      setEnded(false);
      setReplaying(false);
    } catch (e) {
      // Playback failed — keep the Replay button visible so the visitor can retry
      // instead of leaving an invisible overlay blocking the player.
      setReplaying(false);
    }
  };

  // dnt=1 (Do Not Track), no autoplay, no loop, minimal branding. Fullscreen via iframe allow.
  const src = `https://player.vimeo.com/video/${videoId}?dnt=1&badge=0&autoplay=0&loop=0&title=0&byline=0&portrait=0`;

  return (
    <>
      <iframe
        ref={iframeRef}
        src={src}
        title={title}
        className="absolute inset-0 h-full w-full"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
        allowFullScreen
        loading={lazy ? "lazy" : undefined}
        data-testid={testid}
      />
      {ended && (
        <button
          type="button"
          onClick={replay}
          disabled={replaying}
          data-testid={testid ? `${testid}-replay` : "vimeo-replay"}
          className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 backdrop-blur-[2px] transition-opacity disabled:cursor-wait"
        >
          <span className="flex items-center gap-2 rounded-full bg-white/95 px-5 py-2.5 text-sm font-semibold text-re-charcoal shadow-lg">
            <RotateCcw className={`h-4 w-4 text-re-red ${replaying ? "animate-spin" : ""}`} /> {t("common.replay")}
          </span>
        </button>
      )}
    </>
  );
}
